import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Paper,
} from '@material-ui/core';
import {
    getGmailStatus,
    getGmailAuthUrl,
    disconnectGmail,
    syncGmail,
    renewGmailWatches,
    GmailStatus,
} from '../core/services/import.service';

interface GmailSettingsProps {
    userId: string;
}

/**
 * GmailSettings renders the Gmail connection management panel.
 * Shows connect/disconnect controls and sync button based on connection state.
 */
export const GmailSettings: React.FC<GmailSettingsProps> = ({ userId }) => {
    const [status, setStatus] = useState<GmailStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [syncMessage, setSyncMessage] = useState<string | null>(null);

    const fetchStatus = async () => {
        setLoading(true);
        setError(null);
        try {
            const s = await getGmailStatus(userId);
            setStatus(s);
        } catch {
            setError('Failed to load Gmail connection status.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const handleConnect = async () => {
        setActionLoading(true);
        setError(null);
        try {
            const { url } = await getGmailAuthUrl(userId);
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch {
            setError('Failed to get Gmail authorization URL.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDisconnect = async () => {
        setActionLoading(true);
        setError(null);
        try {
            await disconnectGmail(userId);
            await fetchStatus();
        } catch {
            setError('Failed to disconnect Gmail.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleSync = async () => {
        setActionLoading(true);
        setError(null);
        setSyncMessage(null);
        try {
            const result = await syncGmail(userId);
            setSyncMessage(`Synced ${result.synced} message${result.synced !== 1 ? 's' : ''}.`);
            // Refresh status so draft count badge updates
            const refreshed = await getGmailStatus(userId);
            setStatus(refreshed);
        } catch {
            setError('Gmail sync failed. Please try again.');
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <Paper
            style={{
                borderRadius: 8,
                border: '1px solid #e2e8f0',
                padding: '12px 16px',
                backgroundColor: 'white',
            }}
            elevation={0}
        >
            <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Typography
                    style={{
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        color: '#1e293b',
                        letterSpacing: '0.3px',
                    }}
                >
                    Gmail Integration
                </Typography>
                {(loading || actionLoading) && (
                    <CircularProgress size={14} style={{ color: '#7c3aed' }} />
                )}
            </Box>

            {error && (
                <Box style={{ marginBottom: 8, padding: '4px 8px', backgroundColor: '#fee2e2', borderRadius: 4 }}>
                    <Typography style={{ fontSize: '0.72rem', color: '#dc2626' }}>{error}</Typography>
                </Box>
            )}

            {syncMessage && (
                <Box style={{ marginBottom: 8, padding: '4px 8px', backgroundColor: '#dcfce7', borderRadius: 4 }}>
                    <Typography style={{ fontSize: '0.72rem', color: '#16a34a' }}>{syncMessage}</Typography>
                </Box>
            )}

            {loading ? null : status && status.connected ? (
                /* Connected state */
                <Box>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                        <Box
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: '#16a34a',
                                flexShrink: 0,
                            }}
                        />
                        <Typography style={{ fontSize: '0.78rem', color: '#475569' }}>
                            {status.email}
                        </Typography>
                        {status.draftCount > 0 && (
                            <Box
                                style={{
                                    backgroundColor: '#7c3aed',
                                    color: 'white',
                                    borderRadius: 10,
                                    padding: '1px 7px',
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                }}
                            >
                                {status.draftCount} pending
                            </Box>
                        )}
                    </Box>

                    {(() => {
                        const watchActive = status.watchExpiry && new Date(status.watchExpiry) > new Date();
                        const expiryLabel = status.watchExpiry
                            ? new Date(status.watchExpiry).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : null;
                        return (
                            <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                                <Box style={{
                                    width: 6, height: 6, borderRadius: '50%',
                                    backgroundColor: watchActive ? '#16a34a' : '#f59e0b',
                                    flexShrink: 0,
                                }} />
                                <Typography style={{ fontSize: '0.68rem', color: watchActive ? '#16a34a' : '#f59e0b', fontWeight: 600 }}>
                                    {watchActive ? `Auto-sync active until ${expiryLabel}` : 'Auto-sync expired'}
                                </Typography>
                                {!watchActive && (
                                    <Button
                                        size="small"
                                        onClick={async () => {
                                            setActionLoading(true);
                                            try {
                                                await renewGmailWatches(userId);
                                                const refreshed = await getGmailStatus(userId);
                                                setStatus(refreshed);
                                                setSyncMessage('Auto-sync renewed.');
                                            } catch {
                                                setError('Failed to renew auto-sync.');
                                            } finally {
                                                setActionLoading(false);
                                            }
                                        }}
                                        disabled={actionLoading}
                                        style={{ fontSize: '0.65rem', textTransform: 'none', color: '#f59e0b', padding: '1px 6px', minWidth: 0 }}
                                    >
                                        Renew
                                    </Button>
                                )}
                            </Box>
                        );
                    })()}

                    <Box style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleSync}
                            disabled={actionLoading}
                            style={{
                                fontSize: '0.72rem',
                                textTransform: 'none',
                                borderColor: '#7c3aed',
                                color: '#7c3aed',
                                padding: '3px 10px',
                            }}
                        >
                            Sync now
                        </Button>
                        <Button
                            size="small"
                            onClick={handleDisconnect}
                            disabled={actionLoading}
                            style={{
                                fontSize: '0.72rem',
                                textTransform: 'none',
                                color: '#dc2626',
                                padding: '3px 8px',
                            }}
                        >
                            Disconnect
                        </Button>
                    </Box>
                </Box>
            ) : (
                /* Disconnected state */
                <Box>
                    <Typography style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 10 }}>
                        Connect Gmail to automatically import transactions from bank alert emails.
                    </Typography>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={handleConnect}
                        disabled={actionLoading}
                        style={{
                            fontSize: '0.72rem',
                            textTransform: 'none',
                            backgroundColor: '#7c3aed',
                            color: 'white',
                            padding: '4px 12px',
                        }}
                    >
                        Connect Gmail
                    </Button>
                </Box>
            )}
        </Paper>
    );
};
